<template>
  <div class="table-responsive v-table-scroll">
    <table class="table table-hover">
      <thead>
        <tr>
          <th
            v-for="column in columns"
            :key="column.key"
            scope="col"
            :class="{ 'v-table-sortable-header': isColumnSortable(column) }"
            @click="onHeaderClick(column)"
          >
            <template v-if="isColumnSortable(column)">
              <span class="d-inline-flex align-items-center">
                {{ column.label }}
                <span class="v-table-sort-icons ms-2">
                  <i
                    class="fas fa-sort-up"
                    :class="{ active: isActiveDirection(column, 'asc') }"
                  />
                  <i
                    class="fas fa-sort-down"
                    :class="{ active: isActiveDirection(column, 'desc') }"
                  />
                </span>
              </span>
            </template>
            <template v-else>
              {{ column.label }}
            </template>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="items.length === 0">
              <td :colspan="columns.length" class="text-center">
                  <slot name="no-data">
                      No Records Found
                  </slot>
              </td>
        </tr>
        <tr
          v-for="(item, index) in items"
          :key="resolveRowKey(item, index)"
        >
          <td
            v-for="column in columns"
            :key="column.key"
          >
            <slot
              :name="column.key"
              :item="item"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  name: 'VTable',
  props: {
    columns: {
      type: Array,
      default: () => [],
      required: true
    },
    items: {
      type: Array,
      default: () => [],
      required: true
    },
    rowKey: {
      type: [String, Function],
      default: 'id'
    },
    sortable: {
      type: Boolean,
      default: false
    },
    sortKey: {
      type: String,
      default: null
    },
    sortOrder: {
      type: String,
      default: null,
      validator: v => v === null || v === 'asc' || v === 'desc'
    }
  },
  methods: {
    resolveRowKey (item, index) {
      const key = typeof this.rowKey === 'function'
        ? this.rowKey(item, index)
        : item?.[this.rowKey]
      if (process.env.NODE_ENV !== 'production' && (key === undefined || key === null)) {
        // eslint-disable-next-line no-console
        console.warn('[VTable] row key resolved to null/undefined — falling back to index. Pass a valid `row-key` prop.', { item, rowKey: this.rowKey })
      }
      return key ?? `__idx_${index}`
    },
    isColumnSortable (column) {
      return this.sortable && column?.sortable !== false
    },
    isActiveDirection (column, direction) {
      return this.sortKey === column.key && this.sortOrder === direction
    },
    onHeaderClick (column) {
      if (!this.isColumnSortable(column)) return
      this.$emit('sort', column.key)
    }
  }
}
</script>

<style scoped>
/* Bootstrap's .table-responsive only scrolls when it wraps the table (not on the
   <table> itself). Keep a min-width so wide order/dashboard tables scroll on tablet
   instead of crushing their columns. */
.v-table-scroll > .table {
  min-width: 720px;
}
.v-table-sortable-header {
  cursor: pointer;
  user-select: none;
}
.v-table-sort-icons {
  position: relative;
  display: inline-block;
  width: 0.75rem;
  height: 1rem;
}
.v-table-sort-icons .fa-sort-up,
.v-table-sort-icons .fa-sort-down {
  position: absolute;
  left: 0;
  color: #c4c4c4;
  font-size: 0.85rem;
  line-height: 1;
}
.v-table-sort-icons .fa-sort-up {
  top: 0;
}
.v-table-sort-icons .fa-sort-down {
  bottom: 0;
}
.v-table-sort-icons .active {
  color: #212529;
}
</style>
